import { CommonModule } from "@angular/common";
import { AfterViewInit, ChangeDetectorRef, Component, DestroyRef, OnInit, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { FormsModule } from "@angular/forms";
import { Subject } from "rxjs";
import { debounceTime, switchMap } from "rxjs/operators";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatNativeDateModule } from "@angular/material/core";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import swal from "sweetalert2";
import { ProyectosService } from "src/app/proyectos/proyectos.service";
import { RiesgosProyectoComponent } from "src/app/proyectos/seguimiento/proyectos/riesgos/riesgos-proyecto.component";
import { ConsultaServiciosComponent } from "src/app/proyectos/seguimiento/servicios/consulta/consulta-servicios.component";

interface OverviewMetric {
  label: string;
  value: string;
  accent?: boolean;
}

type Fase = 'DESARROLLO' | 'QA' | 'PRODUCCION';
type EstadoSubFase = 'pendiente' | 'en-proceso' | 'completado' | 'con-observaciones';

interface SubFase {
  nombre: string;
  estado: EstadoSubFase;
  opcional?: boolean;
  nota?: string;
}

interface PortfolioCardBase {
  id: string;
  client: string;
  title: string;
  status: string;
  statusTone: "success" | "warning" | "danger" | "neutral";
  plannedProgress: number;
  progress: number;
  note: string;
  noteDate: string;
  owner: string;
  fase: Fase;
  avanceDesarrollo: number | null;
  qaSubFases: SubFase[] | null;
  prodSubFases: SubFase[] | null;
}

interface PortfolioCard extends PortfolioCardBase {
  statusRisk: "green" | "yellow" | "red";
  riskCount: number;
}

interface ResumenSeguimientoResponse {
  totalProyectos: number;
  totalDepartamentos: number;
  projects: PortfolioCardBase[];
  riskCounts?: Record<string, number>;
}

interface FilterOption {
  id: number | null;
  text: string;
}

interface FiltrosSeguimientoResponse {
  clients: FilterOption[];
  leaders: FilterOption[];
}

type PaginationItem = number | "...";

@Component({
  selector: "app-consulta-proyectos",
  standalone: true,
  templateUrl: "./consulta-proyectos.component.html",
  styleUrls: ["./consulta-proyectos.component.css"],
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    MatButtonModule,
    MatDatepickerModule,
    MatDialogModule,
    MatIconModule,
    MatInputModule,
    MatNativeDateModule,
    ConsultaServiciosComponent,
  ],
})
export class ConsultaProyectosComponent implements OnInit, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly filtrosCambio$ = new Subject<void>();
  // Evita que la restauración inicial de filtros (desde query params) dispare
  // llamadas HTTP antes de que el ciclo de vida del componente haya terminado.
  private filtersReady = false;

  activeTab: "proyectos" | "servicios" = "proyectos";

  setTab(tab: "proyectos" | "servicios"): void {
    this.activeTab = tab;
  }

  overviewMetrics: OverviewMetric[] = [
    { label: "Activos", value: "0", accent: true },
  ];

  selectedStatus = "Activo";
  selectedClientId: number | null = null;
  selectedLeaderId: number | null = null;
  selectedDate = "";
  selectedDateValue: Date | null = null;
  isLoading = false;

  readonly statusOptions = ["Todos", "Activo", "Pausado", "Cierre"];
  readonly defaultClientOption: FilterOption = { id: null, text: "Todos" };
  readonly defaultLeaderOption: FilterOption = { id: null, text: "Todos" };

  portfolioCards: PortfolioCard[] = [];
  filteredPortfolioCards: PortfolioCard[] = [];
  paginatedPortfolioCards: PortfolioCard[] = [];
  paginationItems: PaginationItem[] = [];
  clientOptions: FilterOption[] = [this.defaultClientOption];
  leaderOptions: FilterOption[] = [this.defaultLeaderOption];
  riskCountsByProject: Record<string, number> = {};
  currentPage = 1;
  readonly cardsPerPage = 3;

  constructor(
    private proyectoService: ProyectosService,
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog
  ) {}

  ngAfterViewInit(): void {
    // Se activa un tick después de que todos los hooks síncronos del ciclo de vida
    // hayan corrido, para que la restauración inicial de filtros no dispare los
    // manejadores de cambio.
    setTimeout(() => { this.filtersReady = true; });
  }

  ngOnInit(): void {
    this.restaurarFiltrosDesdeQueryParams();
    this.consultarFiltrosSeguimiento();

    this.filtrosCambio$.pipe(
      debounceTime(300),
      switchMap(() =>
        this.proyectoService.consultarResumenSeguimientoProyectos(this.buildParams())
      ),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe({
      next: (response: ResumenSeguimientoResponse) => {
        this.riskCountsByProject = response?.riskCounts ?? {};
        this.portfolioCards = (response?.projects ?? []).map((project) => ({
          ...project,
          fase: (project as any).fase ?? 'DESARROLLO',
          avanceDesarrollo: (project as any).avanceDesarrollo ?? null,
          qaSubFases: (project as any).qaSubFases ?? null,
          prodSubFases: (project as any).prodSubFases ?? null,
          statusRisk: this.calcularStatusRisk(project),
          riskCount: this.obtenerConteoRiesgos(project.id),
        }));
        this.filteredPortfolioCards = [...this.portfolioCards];
        this.currentPage = 1;
        this.actualizarPaginacion();
        this.actualizarMetricas();
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error("Error al consultar el resumen de seguimiento de proyectos:", error);
        this.portfolioCards = [];
        this.filteredPortfolioCards = [];
        this.paginatedPortfolioCards = [];
        this.paginationItems = [];
        this.currentPage = 1;
        this.overviewMetrics = [
          { label: this.obtenerEtiquetaChipEstatus(), value: "0", accent: true },
        ];
        this.isLoading = false;
        this.cdr.detectChanges();
      },
    });

    this.emitirCambioFiltros();
  }

  onStatusChange(): void {
    if (!this.filtersReady) return;
    this.selectedClientId = null;
    this.selectedLeaderId = null;
    this.actualizarQueryParams();
    this.emitirCambioFiltros();
  }

  onClientChange(): void {
    if (!this.filtersReady) return;
    this.actualizarQueryParams();
    this.emitirCambioFiltros();
  }

  onLeaderChange(): void {
    if (!this.filtersReady) return;
    this.actualizarQueryParams();
    this.emitirCambioFiltros();
  }

  onDateChange(fecha: Date | null): void {
    this.selectedDate = this.formatearFecha(fecha);
    if (!this.filtersReady) return;
    this.actualizarQueryParams();
    this.emitirCambioFiltros();
  }

  // Un <label> que envuelve un <select> solo lo enfoca al hacer click en Chrome/Edge
  // (no abre la lista); un click directo sobre el <select> sí la abre de forma nativa.
  // Para que TODO el chip (ícono, texto, flecha) abra la lista y no solo el <select>,
  // se fuerza la apertura con showPicker() cuando el click no fue directamente sobre él.
  abrirFiltro(select: HTMLSelectElement, event: MouseEvent): void {
    if (event.target === select || select.disabled) {
      return;
    }

    select.focus();
    if (typeof (select as any).showPicker === "function") {
      try {
        (select as any).showPicker();
      } catch {
        // showPicker puede lanzar si el navegador no permite abrirlo en este contexto;
        // el foco ya aplicado permite abrirlo con teclado como respaldo.
      }
    }
  }

  private buildParams() {
    return {
      estatus: this.selectedStatus,
      idCliente: this.selectedClientId,
      idLider: this.selectedLeaderId,
      fecha: this.selectedDate || null,
    };
  }

  private emitirCambioFiltros(): void {
    this.isLoading = true;
    this.filtrosCambio$.next();
  }

  getDetailQueryParams() {
    return {
      estatus: this.selectedStatus,
      idCliente: this.selectedClientId,
      idLider: this.selectedLeaderId,
      fecha: this.selectedDate || null,
    };
  }

  get totalPages(): number {
    const total = Math.ceil(this.filteredPortfolioCards.length / this.cardsPerPage);
    return total > 0 ? total : 1;
  }

  trackByCardId(_: number, card: PortfolioCard): string {
    return card.id;
  }

  trackByOptionId(_: number, option: FilterOption): number | null {
    return option.id;
  }

  trackByPaginationItem(index: number, _: PaginationItem): number {
    return index;
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) {
      return;
    }
    this.currentPage = page;
    this.actualizarPaginacion();
  }

  private actualizarPaginacion(): void {
    const total = this.totalPages;
    const current = this.currentPage;

    const startIndex = (current - 1) * this.cardsPerPage;
    this.paginatedPortfolioCards = this.filteredPortfolioCards.slice(
      startIndex,
      startIndex + this.cardsPerPage
    );

    if (total <= 7) {
      this.paginationItems = Array.from({ length: total }, (_, i) => i + 1);
      return;
    }
    if (current <= 3) {
      this.paginationItems = [1, 2, 3, "...", total];
      return;
    }
    if (current >= total - 2) {
      this.paginationItems = [1, "...", total - 2, total - 1, total];
      return;
    }
    this.paginationItems = [1, "...", current - 1, current, current + 1, "...", total];
  }

  onPaginationItemClick(item: PaginationItem): void {
    if (item === "...") {
      return;
    }
    this.goToPage(item);
  }

  goToNextPage(): void {
    this.goToPage(this.currentPage + 1);
  }

  goToPreviousPage(): void {
    this.goToPage(this.currentPage - 1);
  }

  exportarPdf(): void {
    if (this.filteredPortfolioCards.length === 0) {
      swal.fire("Atención", "No hay proyectos para exportar con los filtros actuales", "warning");
      return;
    }

    const encabezados = [
      "Proyecto",
      "Cliente",
      "Área",
      "Estatus",
      "Avance planeado %",
      "Avance real %",
      "Fase",
      "Riesgos",
      "Responsable",
      "Nota",
      "Fecha nota",
    ];

    const filas = this.filteredPortfolioCards.map((card) => [
      card.title,
      card.client,
      (card as any).area ?? "",
      card.status,
      card.plannedProgress,
      card.progress,
      this.getFaseLabel(card.fase),
      card.riskCount,
      card.owner,
      card.note,
      card.noteDate,
    ]);

    const doc = new jsPDF("l", "mm", "a4");
    const fechaGeneracion = new Date().toLocaleString("es-MX", { dateStyle: "long", timeStyle: "short" });

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(183, 28, 28);
    doc.text("Reporte de Seguimiento de Proyectos", 14, 15);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(110, 116, 128);
    doc.text(`Generado el ${fechaGeneracion}`, 14, 21);

    autoTable(doc, {
      head: [encabezados],
      body: filas,
      startY: 26,
      styles: { fontSize: 7.5, cellPadding: 2 },
      headStyles: { fillColor: [183, 28, 28], textColor: 255, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [247, 249, 251] },
    });

    const fechaHoy = new Date().toISOString().slice(0, 10);
    doc.save(`Seguimiento_Proyectos_${fechaHoy}.pdf`);
  }

  getRiskLabel(statusRisk: "green" | "yellow" | "red"): string {
    if (statusRisk === "green") return "EN TIEMPO";
    if (statusRisk === "yellow") return "EN RIESGO";
    return "CRíTICO";
  }

  getRealProgressBarClass(project: PortfolioCard): string {
    if (project.statusRisk === "green") return "progress-track__value--risk-green";
    if (project.statusRisk === "yellow") return "progress-track__value--risk-yellow";
    return "progress-track__value--risk-red";
  }

  getPlannedProgressBarClass(project: PortfolioCard): string {
    const status = (project.status || "").trim().toLowerCase();

    if (status === "activo") return "progress-track__value--success";
    if (status === "pausado") return "progress-track__value--warning";
    if (status === "cierre") return "progress-track__value--danger";

    return "progress-track__value--neutral";
  }

  getFaseLabel(fase: Fase): string {
    const labels: Record<Fase, string> = { DESARROLLO: 'Desarrollo', QA: 'QA', PRODUCCION: 'Producción' };
    return labels[fase] ?? 'Desarrollo';
  }

  getFaseIcon(fase: Fase): string {
    const icons: Record<Fase, string> = { DESARROLLO: 'code', QA: 'science', PRODUCCION: 'rocket_launch' };
    return icons[fase] ?? 'code';
  }

  getFaseBadgeClass(fase: Fase): string {
    const classes: Record<Fase, string> = {
      DESARROLLO: 'phase-badge--desarrollo',
      QA: 'phase-badge--qa',
      PRODUCCION: 'phase-badge--produccion',
    };
    return classes[fase] ?? 'phase-badge--desarrollo';
  }

  getActiveSubFases(project: PortfolioCard): SubFase[] {
    const raw: any[] | null =
      project.fase === 'QA'         ? (project.qaSubFases   ?? null) :
      project.fase === 'PRODUCCION' ? (project.prodSubFases ?? null) : null;

    if (!raw?.length) {
      return project.fase === 'QA' ? this.getDefaultQaSubFases() : this.getDefaultProdSubFases();
    }

    // Soporta modelo nuevo {completado: boolean} y modelo viejo {estado: string}
    return raw.map((sf: any): SubFase => ({
      nombre:   sf.nombre  ?? '',
      estado:   sf.estado  ?? (sf.completado === true ? 'completado' : 'pendiente'),
      opcional: sf.opcional,
      nota:     sf.nota,
    }));
  }

  isNextSubFaseOpcional(project: PortfolioCard, index: number): boolean {
    return this.getActiveSubFases(project)[index + 1]?.opcional === true;
  }

  getNoteLabel(fase: Fase): string {
    const labels: Record<Fase, string> = {
      DESARROLLO: 'Último seguimiento',
      QA: 'Estado actual',
      PRODUCCION: 'Notas de salida',
    };
    return labels[fase] ?? 'Último seguimiento';
  }

  private getDefaultQaSubFases(): SubFase[] {
    return [
      { nombre: 'Solicitud',  estado: 'pendiente' },
      { nombre: 'Ejecución',  estado: 'pendiente' },
      { nombre: 'Corrección', estado: 'pendiente', opcional: true, nota: 'Solo si hay defectos' },
      { nombre: 'Vobo',       estado: 'pendiente' },
    ];
  }

  private getDefaultProdSubFases(): SubFase[] {
    return [
      { nombre: 'Validación', estado: 'pendiente' },
      { nombre: 'Vobo',       estado: 'pendiente' },
    ];
  }

  openProjectRisks(project: PortfolioCard): void {
    if (!project.riskCount) {
      return;
    }

    this.dialog.open(RiesgosProyectoComponent, {
      autoFocus: false,
      width: "1180px",
      maxWidth: "96vw",
      maxHeight: "90vh",
      data: {
        projectId: project.id,
        projectTitle: project.title,
        riskCount: project.riskCount,
        projectClient: project.client,
        projectLeader: project.owner,
      },
    });
  }

  private consultarFiltrosSeguimiento(): void {
    this.proyectoService.consultarFiltrosSeguimientoProyectos().pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe({
      next: (response: FiltrosSeguimientoResponse) => {
        this.clientOptions = this.construirOpciones(
          response?.clients ?? [],
          this.defaultClientOption
        );
        this.leaderOptions = this.construirOpciones(
          response?.leaders ?? [],
          this.defaultLeaderOption
        );
      },
      error: (error) => {
        console.error(
          "Error al consultar los filtros de seguimiento de proyectos:",
          error
        );
        this.clientOptions = [this.defaultClientOption];
        this.leaderOptions = [this.defaultLeaderOption];
      },
    });
  }

  private construirOpciones(
    opciones: FilterOption[],
    opcionDefault: FilterOption
  ): FilterOption[] {
    const opcionesValidas = (opciones ?? [])
      .filter((option) => option?.id != null && !!option?.text?.trim())
      .map((option) => ({ id: option.id, text: option.text.trim() }))
      .filter(
        (option, index, array) =>
          array.findIndex((item) => item.id === option.id) === index
      )
      .sort((a, b) => a.text.localeCompare(b.text));

    return [opcionDefault, ...opcionesValidas];
  }

  private actualizarMetricas(): void {
    this.overviewMetrics = [
      {
        label: this.obtenerEtiquetaChipEstatus(),
        value: String(this.filteredPortfolioCards.length),
        accent: true,
      },
    ];

    if (!this.portfolioCards.length) {
      this.overviewMetrics = [
        { label: this.obtenerEtiquetaChipEstatus(), value: "0", accent: true },
      ];
    }
  }

  private obtenerEtiquetaChipEstatus(): string {
    return this.selectedStatus?.toLowerCase() === "todos"
      ? "Proyectos"
      : "Activos";
  }

  private restaurarFiltrosDesdeQueryParams(): void {
    const queryParams = this.route.snapshot.queryParamMap;
    this.selectedStatus = queryParams.get("estatus") || this.selectedStatus;
    this.selectedClientId = this.parseNullableNumber(queryParams.get("idCliente"));
    this.selectedLeaderId = this.parseNullableNumber(queryParams.get("idLider"));
    this.selectedDate = queryParams.get("fecha") || "";
    this.selectedDateValue = this.obtenerFechaDesdeTexto(this.selectedDate);
  }

  private obtenerFechaDesdeTexto(fecha: string): Date | null {
    const [anio, mes, dia] = fecha.split("-").map(Number);

    if (!anio || !mes || !dia) {
      return null;
    }

    return new Date(anio, mes - 1, dia);
  }

  private formatearFecha(fecha: Date | null): string {
    if (!fecha) {
      return "";
    }

    const anio = fecha.getFullYear();
    const mes = String(fecha.getMonth() + 1).padStart(2, "0");
    const dia = String(fecha.getDate()).padStart(2, "0");

    return `${anio}-${mes}-${dia}`;
  }

  private actualizarQueryParams(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: this.getDetailQueryParams(),
      queryParamsHandling: "merge",
      replaceUrl: true,
    });
  }

  private parseNullableNumber(value: string | null): number | null {
    if (value == null || value === "") {
      return null;
    }

    const parsedValue = Number(value);
    return Number.isNaN(parsedValue) ? null : parsedValue;
  }

  private calcularStatusRisk(project: PortfolioCardBase): "green" | "yellow" | "red" {
    const planned = Number(project.plannedProgress ?? 0);
    const real = Number(project.progress ?? 0);

    const retraso = Math.max(planned - real, 0);

    if (retraso >= 15) return "red";
    if (retraso >= 7)  return "yellow";
    return "green";
  }

  private obtenerConteoRiesgos(projectId: string): number {
    return Number(this.riskCountsByProject?.[projectId] ?? 0);
  }

}
